import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { SurfaceCard } from "@/components/common/Cards";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/common/StatusBadge";
import {
  ArrowLeft, Save, Plus, Type, AlignLeft, Hash, ToggleLeft, CircleDot, CheckSquare,
  ChevronDown, Calendar, Ruler, Trash2, GripVertical, Eye, Sparkles, Mic,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const QTYPES = [
  { type: "short_text", label: "Short Text", icon: Type },
  { type: "long_text", label: "Long Text", icon: AlignLeft },
  { type: "number", label: "Number", icon: Hash },
  { type: "yes_no", label: "Yes / No", icon: ToggleLeft },
  { type: "single_choice", label: "Single Choice", icon: CircleDot },
  { type: "multiple_choice", label: "Multiple Choice", icon: CheckSquare },
  { type: "dropdown", label: "Dropdown", icon: ChevronDown },
  { type: "date", label: "Date", icon: Calendar },
  { type: "measurement", label: "Clinical Measurement", icon: Ruler },
];
const CATEGORIES = ["Consultation", "Disease", "Condition", "Treatment", "Procedure", "Clinical Category", "Custom"];
const HAS_OPTIONS = ["single_choice", "multiple_choice", "dropdown"];

const uid = () => Math.random().toString(36).slice(2, 10);

export default function FormBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(false);
  const [conditions, setConditions] = useState([]);
  useEffect(() => { api.get("/conditions").then((r) => setConditions(r.data)).catch(() => {}); }, []);

  useEffect(() => { api.get(`/forms/${id}`).then((r) => { setForm(r.data); setSelected(r.data.questions?.[0]?.id || null); }); }, [id]);
  if (!form) return <Skeleton className="h-[70vh] rounded-2xl" />;

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const addQuestion = (type) => {
    const q = { id: uid(), type, label: `New ${type.replace(/_/g, " ")} question`, required: false, options: HAS_OPTIONS.includes(type) ? ["Option 1", "Option 2"] : undefined, ai_interpret: false, voice_capturable: true };
    update({ questions: [...form.questions, q] });
    setSelected(q.id);
  };
  const updateQuestion = (qid, patch) => update({ questions: form.questions.map((q) => (q.id === qid ? { ...q, ...patch } : q)) });
  const removeQuestion = (qid) => { update({ questions: form.questions.filter((q) => q.id !== qid) }); if (selected === qid) setSelected(null); };
  const save = async () => { try { await api.put(`/forms/${id}`, form); toast.success("Form saved"); } catch { toast.error("Could not save the form"); } };
  const setDefault = async () => { await api.post(`/forms/${id}/set-default`); update({ is_default: true, status: "active" }); toast.success("Set as the default form for this condition"); };

  const sel = form.questions.find((q) => q.id === selected);
  const iconFor = (t) => (QTYPES.find((x) => x.type === t)?.icon || Type);

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <button onClick={() => navigate("/forms")} className="flex items-center gap-1.5 text-sm font-semibold text-[#687173] dark:text-[#8B9698] hover:text-[#101516] dark:hover:text-white"><ArrowLeft className="h-4 w-4" />Forms</button>
        <div className="flex items-center gap-2">
          <StatusBadge status={form.status} />
          <Button variant="outline" onClick={() => setPreview(!preview)} data-testid="form-preview-btn" className="rounded-lg border-[#E2E8F0] dark:border-[#223139]"><Eye className="h-4 w-4 mr-1.5" />{preview ? "Edit" : "Preview"}</Button>
          <Button onClick={save} data-testid="form-save-btn" className="bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg"><Save className="h-4 w-4 mr-1.5" />Save</Button>
        </div>
      </div>

      {preview ? (
        <div className="mx-auto max-w-2xl">
          <SurfaceCard className="p-8">
            <h2 className="text-xl font-bold text-[#101516] dark:text-white">{form.title}</h2>
            {form.description && <p className="text-sm text-[#687173] dark:text-[#8B9698] mt-1">{form.description}</p>}
            <div className="mt-6 space-y-5">
              {form.questions.map((q) => (
                <div key={q.id}>
                  <Label className="text-sm font-semibold text-[#101516] dark:text-white">{q.label}{q.required && <span className="text-red-500 ml-1">*</span>}</Label>
                  <div className="mt-1.5">
                    {q.type === "long_text" ? <Textarea disabled rows={3} /> :
                     q.type === "yes_no" ? <div className="flex gap-2"><Button variant="outline" size="sm" disabled>Yes</Button><Button variant="outline" size="sm" disabled>No</Button></div> :
                     HAS_OPTIONS.includes(q.type) ? <div className="space-y-1.5">{(q.options || []).map((o, i) => <div key={i} className="flex items-center gap-2 text-sm text-[#687173]"><span className="h-4 w-4 rounded-full border border-[#E2E8F0]" />{o}</div>)}</div> :
                     <Input disabled type={q.type === "number" || q.type === "measurement" ? "number" : q.type === "date" ? "date" : "text"} placeholder={q.type === "measurement" ? "mm" : ""} />}
                  </div>
                </div>
              ))}
              {form.questions.length === 0 && <p className="text-sm text-[#8B9698] text-center py-8">No questions yet. Switch to edit mode to add questions.</p>}
            </div>
          </SurfaceCard>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr_300px] gap-5">
          {/* LEFT: elements + structure */}
          <div className="space-y-5">
            <SurfaceCard className="p-4">
              <div className="text-[11px] font-bold uppercase tracking-wider text-[#8B9698] mb-3">Add Question</div>
              <div className="grid grid-cols-2 gap-2">
                {QTYPES.map((qt) => (
                  <button key={qt.type} onClick={() => addQuestion(qt.type)} data-testid={`add-${qt.type}`} className="flex flex-col items-center gap-1.5 rounded-lg border border-[#E2E8F0] dark:border-[#223139] p-2.5 text-[10px] font-medium text-[#687173] dark:text-[#8B9698] hover:border-[#00A85A]/40 hover:text-[#00A85A] transition-colors">
                    <qt.icon className="h-4 w-4" />{qt.label}
                  </button>
                ))}
              </div>
            </SurfaceCard>
          </div>

          {/* CENTER: canvas */}
          <SurfaceCard className="p-6">
            <Input value={form.title} onChange={(e) => update({ title: e.target.value })} data-testid="form-title-input" className="border-0 border-b border-[#E2E8F0] dark:border-[#223139] rounded-none px-0 text-lg font-bold focus-visible:ring-0" placeholder="Form title" />
            <Textarea value={form.description || ""} onChange={(e) => update({ description: e.target.value })} rows={1} className="border-0 px-0 text-sm resize-none focus-visible:ring-0 mt-2" placeholder="Form description..." />
            <div className="mt-5 space-y-2.5">
              {form.questions.map((q, i) => {
                const Icon = iconFor(q.type);
                return (
                  <button key={q.id} onClick={() => setSelected(q.id)} data-testid={`question-item-${i}`}
                    className={cn("flex w-full items-center gap-3 rounded-xl border p-3.5 text-left transition-colors",
                      selected === q.id ? "border-[#00A85A] bg-[#F4FBF7] dark:bg-[#00A85A]/10" : "border-[#E2E8F0] dark:border-[#223139] hover:border-[#00A85A]/40")}>
                    <GripVertical className="h-4 w-4 text-[#8B9698] shrink-0" />
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#E8F7F0] dark:bg-[#00A85A]/15 shrink-0"><Icon className="h-4 w-4 text-[#00A85A]" /></div>
                    <div className="min-w-0 flex-1"><div className="text-sm font-semibold text-[#101516] dark:text-white truncate">{q.label}</div><div className="text-[10px] text-[#8B9698] capitalize">{q.type.replace(/_/g, " ")}{q.required ? " · Required" : ""}</div></div>
                    <Trash2 onClick={(e) => { e.stopPropagation(); removeQuestion(q.id); }} className="h-4 w-4 text-[#8B9698] hover:text-red-500 shrink-0" />
                  </button>
                );
              })}
              {form.questions.length === 0 && <div className="rounded-xl border border-dashed border-[#E2E8F0] dark:border-[#223139] py-12 text-center text-sm text-[#8B9698]">Add questions from the left panel</div>}
            </div>
          </SurfaceCard>

          {/* RIGHT: properties */}
          <SurfaceCard className="p-5 h-fit lg:sticky lg:top-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[11px] font-bold uppercase tracking-wider text-[#8B9698]">{sel ? "Question" : "Form"} Settings</div>
              <button onClick={() => setSelected(null)} data-testid="form-settings-btn" className="text-[11px] font-semibold text-[#00A85A] hover:underline">Form settings</button>
            </div>
            {!sel ? (
              <div className="space-y-4">
                <div><Label className="text-xs font-semibold">Category</Label>
                  <Select value={form.category} onValueChange={(v) => update({ category: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="form-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((cat) => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs font-semibold">Status</Label>
                  <Select value={form.status} onValueChange={(v) => update({ status: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="archived">Archived</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs font-semibold">Condition</Label>
                  <Select value={form.condition_slug || ""} onValueChange={(v) => update({ condition_slug: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="form-condition-select"><SelectValue placeholder="Select condition" /></SelectTrigger>
                    <SelectContent>{conditions.map((cd) => <SelectItem key={cd.id} value={cd.slug}>{cd.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-[#E2E8F0] dark:border-[#223139] p-3"><Label className="text-xs font-semibold">Default for condition</Label>{form.is_default ? <StatusBadge status="success" label="Default" /> : <Button size="sm" variant="outline" onClick={setDefault} data-testid="form-set-default" className="h-7 rounded-md">Set default</Button>}</div>
                <p className="text-xs text-[#8B9698] pt-2">Select a question to edit its settings.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div><Label className="text-xs font-semibold">Question label</Label><Input value={sel.label} onChange={(e) => updateQuestion(sel.id, { label: e.target.value })} data-testid="question-label-input" className="mt-1.5" /></div>
                <div><Label className="text-xs font-semibold">Type</Label>
                  <Select value={sel.type} onValueChange={(v) => updateQuestion(sel.id, { type: v, options: HAS_OPTIONS.includes(v) ? sel.options || ["Option 1"] : undefined })}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{QTYPES.map((qt) => <SelectItem key={qt.type} value={qt.type}>{qt.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {HAS_OPTIONS.includes(sel.type) && (
                  <div><Label className="text-xs font-semibold">Options</Label>
                    <div className="mt-1.5 space-y-2">
                      {(sel.options || []).map((o, i) => (
                        <div key={i} className="flex gap-1.5">
                          <Input value={o} onChange={(e) => { const opts = [...sel.options]; opts[i] = e.target.value; updateQuestion(sel.id, { options: opts }); }} className="h-9" />
                          <button onClick={() => updateQuestion(sel.id, { options: sel.options.filter((_, x) => x !== i) })} className="text-[#8B9698] hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      ))}
                      <Button variant="outline" size="sm" onClick={() => updateQuestion(sel.id, { options: [...(sel.options || []), `Option ${(sel.options?.length || 0) + 1}`] })} className="w-full rounded-lg border-dashed"><Plus className="h-3.5 w-3.5 mr-1" />Add option</Button>
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between rounded-lg border border-[#E2E8F0] dark:border-[#223139] p-3"><Label className="text-xs font-semibold">Required</Label><Switch checked={sel.required} onCheckedChange={(v) => updateQuestion(sel.id, { required: v })} data-testid="question-required-switch" /></div>
                <div className="flex items-center justify-between rounded-lg border border-[#E2E8F0] dark:border-[#223139] p-3">
                  <Label className="text-xs font-semibold flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-[#00A85A]" />AI Interpretation</Label>
                  <Switch checked={sel.ai_interpret} onCheckedChange={(v) => updateQuestion(sel.id, { ai_interpret: v })} />
                </div>
                <div className="rounded-lg border border-[#E2E8F0] dark:border-[#223139] p-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold flex items-center gap-1.5"><Mic className="h-3.5 w-3.5 text-[#00A85A]" />Capturable by voice</Label>
                    <Switch checked={sel.voice_capturable !== false} onCheckedChange={(v) => updateQuestion(sel.id, { voice_capturable: v })} data-testid="question-voice-switch" />
                  </div>
                  <p className="mt-1.5 text-[10px] text-[#8B9698]">Turn off for fields that must be entered/selected manually. In voice mode, CONTAHA asks the doctor to confirm these at the end.</p>
                </div>
              </div>
            )}
          </SurfaceCard>
        </div>
      )}
    </div>
  );
}
